import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { addDays, format, isValid, parse } from "date-fns";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { isMSP } from "../../helpers/util_helper";
import { useAccounts } from "../../components/Common/HelperComponents/GetDataFromStore";
import { getAccountCloudType } from "../../CloudProviderContext";
import { AWS_REGION_CODES } from "../Compliance/AuditHub/utils";
import { AWS_ACCOUNT_NUMBER } from "../Compliance/AuditHub/constants";
import { getCustomerOrgAccounts } from "../../store/customer/customerSlice";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  accentOutlinedButtonSx,
  AuditHubThemeProvider,
  auditHubPageSx,
  outlinedButtonSx,
  primaryButtonSx,
} from "../Compliance/AuditHub/theme";
import { PROGRAM_TYPES } from "./programTypes";
import { fieldsIn, programForm } from "./programForms";
import {
  buildProgramCreatePayload,
  formFromProgramRecord,
  toApiProgramType,
  validateActiveProgram,
} from "./programPayload";
import {
  createPartnerProgram,
  getEligibleServicesMetadata,
  getPartnerProgram,
  updatePartnerProgram,
} from "../../helpers/partnerCentral_helper";
import DueDateField from "../Compliance/AuditHub/DueDateField";
import {
  buildSelectProps,
  fieldSx,
  menuItemSx,
  selectFieldSx,
} from "./formFieldStyles";
import {
  formTableActionBtnSx,
  formTableActionsWrapSx,
  formTableBodyCellSx,
  formTableCardSx,
  formTableDeleteBtnSx,
  formTableHeadCellSx,
} from "./formTableStyles";
import PayerPayeeAccountsField from "./PayerPayeeAccountsField";

const labelSx = {
  display: "block",
  mb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: AH_TEXT,
  lineHeight: 1.4,
};

const hintSx = { mt: 0.5, fontSize: 12, color: AH_MUTED, lineHeight: 1.4 };

/** Same chip treatment as Audit Hub Create Assessment / Add Account regions. */
const regionChipSx = (selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
  color: AH_TEXT,
  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
});

const regionsSummary = (regions) => {
  if (!regions.length) return "All regions";
  return regions.length === 1 ? regions[0] : `${regions.length} regions`;
};

const servicesSummary = (services) => {
  if (!services.length) return "None selected";
  return services.length === 1 ? "1 service" : `${services.length} services`;
};

/** First selectable end date: day after start (yyyy-MM-dd). */
const dayAfterIso = (isoDate) => {
  if (!isoDate) return null;
  const parsed = parse(isoDate, "yyyy-MM-dd", new Date());
  if (!isValid(parsed)) return null;
  return format(addDays(parsed, 1), "yyyy-MM-dd");
};

const serviceChipSx = (selected) => ({
  ...regionChipSx(selected),
  height: "auto",
  minHeight: 28,
  py: 0.5,
  maxWidth: "100%",
  "& .MuiChip-label": {
    whiteSpace: "normal",
    display: "block",
    lineHeight: 1.35,
    py: 0.25,
  },
});

const sectionSx = {
  mt: 2.5,
  mb: 1.5,
  fontSize: 14,
  fontWeight: 700,
  color: AH_TEXT,
};

const emptyTier = () => ({
  id: `tier-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: "",
  startDate: "",
  endDate: "",
  threshold: "",
  discount: "",
});

const emptyYear = () => ({
  id: `year-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label: "",
  startDate: "",
  endDate: "",
  commitment: "",
  discount: "",
});

const emptyForm = () => ({
  programType: PROGRAM_TYPES[0].code,
  displayName: "",
  agreementId: "",
  referenceNumber: "",
  payerAccountNumbers: [],
  payeeAccountNumbers: [],
  payeeUiPayerNumbers: [],
  payerPayeeAccountsMap: {},
  payeePayerByNumber: {},
  regions: [],
  eligibleServices: [],
  qualifyingServices: [],
  riEligibleServices: [],
  startDate: "",
  endDate: "",
  renewalNotification: "",
  expirationAlertWindow: "",
  baselineDiscount: "",
  annualCommitment: "",
  paymentTerms: "",
  partnerTierLevel: "",
  growthTriggerMetric: "",
  targetMilestoneValue: "",
  growthBonusRate: "",
  payoutStructure: "",
  expirationOverride: false,
  trueUpWindow: "",
  multiYearCommitment: "",
  annualConsumptionTarget: "",
  edpDiscount: "",
  trueUpModel: "",
  shortfallRule: "",
  rolloverUnused: false,
  riCommitmentType: "",
  paymentOption: "",
  termLength: "",
  amortizationMethod: "",
  targetCoverage: "",
  wasteThreshold: "",
  crossAccountSharing: false,
  years: [],
  tiers: [],
});

const parseNumber = (value) => {
  const cleaned = String(value || "").replace(/[$,]/g, "").trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const formatCommitment = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return value;
  return amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const FieldLabel = ({ htmlFor, children }) => (
  <Typography component="label" htmlFor={htmlFor} sx={labelSx}>
    {children}
  </Typography>
);

const AddProgram = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const allowed = isMSP();
  const accounts = useAccounts();
  const customerOrgAccountsSuccess = useSelector(
    (state) => state.customer?.customerOrgAccountsSuccess
  );
  const search = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const programId = search.get("id");
  const programTypeParam = search.get("program_type");
  const isEdit = Boolean(programId && programTypeParam);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [eligibleServicesCatalog, setEligibleServicesCatalog] = useState([]);
  const [eligibleServicesLoading, setEligibleServicesLoading] = useState(false);
  const [eligibleServicesError, setEligibleServicesError] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [tierDraft, setTierDraft] = useState(emptyTier);
  const [tierDraftErrors, setTierDraftErrors] = useState({});
  const [editingTierId, setEditingTierId] = useState(null);
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [yearDraft, setYearDraft] = useState(emptyYear);
  const [yearDraftErrors, setYearDraftErrors] = useState({});
  const [editingYearId, setEditingYearId] = useState(null);
  const config = programForm(form.programType);
  const tierVariant = useMemo(
    () => config.sections.flatMap((section) => section.blocks).find((block) => block.kind === "tiers")?.variant || null,
    [config]
  );
  const hasYearsSection = useMemo(
    () => config.sections.flatMap((section) => section.blocks).some((block) => block.kind === "years"),
    [config]
  );
  const tierDialogIsPpa = tierVariant === "ppa";
  const tierDialogIsSpp = tierVariant === "spp";

  useEffect(() => {
    document.title = isEdit
      ? "Edit Program | Partner Central | CloudNova App"
      : "Add Program | Partner Central | CloudNova App";
  }, [isEdit]);

  useEffect(() => {
    if (!allowed) history.replace("/dashboard");
  }, [allowed, history]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setEligibleServicesLoading(true);
    setEligibleServicesError("");
    getEligibleServicesMetadata()
      .then((list) => {
        if (cancelled) return;
        setEligibleServicesCatalog(list);
      })
      .catch((error) => {
        if (cancelled) return;
        setEligibleServicesCatalog([]);
        setEligibleServicesError(
          error?.message || "Failed to load eligible services."
        );
      })
      .finally(() => {
        if (!cancelled) setEligibleServicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => {
    dispatch(getCustomerOrgAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (!allowed || !isEdit) {
      setLoadingRecord(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingRecord(true);
    setFormError("");
    getPartnerProgram(programTypeParam, programId)
      .then((record) => {
        if (cancelled) return;
        setForm(formFromProgramRecord(record));
      })
      .catch((error) => {
        if (cancelled) return;
        setFormError(error?.message || "Unable to load program.");
      })
      .finally(() => {
        if (!cancelled) setLoadingRecord(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, isEdit, programId, programTypeParam]);

  const payerAccounts = useMemo(() => {
    // orgAccount.accounts is null while a list refresh is in flight — treat as empty, not a hard failure.
    if (!Array.isArray(accounts)) return [];
    return accounts
      .filter((account) => {
        const number = String(account?.accountNumber ?? "").trim();
        if (!AWS_ACCOUNT_NUMBER.test(number)) return false;
        // Exclude Azure only; missing/legacy types are treated as AWS elsewhere in the app.
        return getAccountCloudType(account) !== "AZURE";
      })
      .map((account) => ({
        accountNumber: String(account.accountNumber).trim(),
        accountName: account.accountName || "",
      }));
  }, [accounts]);

  /** payee accountNumber → payer accountNumber for payer_payee_accounts grouping. */
  const accountPayerByNumber = useMemo(() => {
    const map = {};
    let data = customerOrgAccountsSuccess?.data;
    if (data && typeof data === "object" && !Array.isArray(data) && data.data && typeof data.data === "object") {
      data = data.data;
    }
    const entries = Array.isArray(data)
      ? data.map((acc, index) => [String(index), acc])
      : data && typeof data === "object"
        ? Object.entries(data)
        : [];
    entries.forEach(([key, acc]) => {
      if (!acc || typeof acc !== "object") return;
      if (key === "status" || key === "message" || key === "error") return;
      const accountNumber = String(
        acc.account_number ?? acc.accountNumber ?? (/^\d{12}$/.test(key) ? key : "")
      ).trim();
      const payerAccountId = String(
        acc.payer_account_id ?? acc.payerAccountId ?? ""
      ).trim();
      if (/^\d{12}$/.test(accountNumber) && /^\d{12}$/.test(payerAccountId)) {
        map[accountNumber] = payerAccountId;
      }
    });
    if (Array.isArray(accounts)) {
      accounts.forEach((account) => {
        const accountNumber = String(account?.accountNumber ?? "").trim();
        const payerAccountId = String(
          account?.payerAccountId ?? account?.payer_account_id ?? ""
        ).trim();
        if (/^\d{12}$/.test(accountNumber) && /^\d{12}$/.test(payerAccountId) && !map[accountNumber]) {
          map[accountNumber] = payerAccountId;
        }
      });
    }
    return map;
  }, [customerOrgAccountsSuccess, accounts]);

  // If Select All marked linked payees as "Payer", demote them once org linkage is known.
  useEffect(() => {
    if (!accountPayerByNumber || !Object.keys(accountPayerByNumber).length) return;
    setForm((prev) => {
      const payers = [
        ...new Set(
          (prev.payerAccountNumbers || [])
            .map((id) => String(id || "").trim())
            .filter((id) => /^\d{12}$/.test(id))
        ),
      ];
      if (payers.length < 2) return prev;
      const payerSet = new Set(payers);
      const truePayers = [];
      const demoted = [];
      payers.forEach((id) => {
        const owner = String(accountPayerByNumber[id] || "").trim();
        if (/^\d{12}$/.test(owner) && owner !== id && payerSet.has(owner)) {
          demoted.push(id);
        } else {
          truePayers.push(id);
        }
      });
      if (!demoted.length) return prev;
      const payees = [
        ...new Set([
          ...(prev.payeeAccountNumbers || [])
            .map((id) => String(id || "").trim())
            .filter((id) => /^\d{12}$/.test(id)),
          ...demoted,
        ]),
      ].filter((id) => !truePayers.includes(id));
      return {
        ...prev,
        payerAccountNumbers: truePayers,
        payeeAccountNumbers: payees,
        // Clear cached map so save rebuilds from the corrected arrays.
        payerPayeeAccountsMap: {},
      };
    });
  }, [accountPayerByNumber]);

  const setField = (key, value) => {
    setForm((prev) => {
      const nextValue = typeof value === "function" ? value(prev[key]) : value;
      return { ...prev, [key]: nextValue };
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const chooseProgram = (programType) => {
    setForm({ ...emptyForm(), programType });
    setErrors({});
    setServiceSearch("");
    setTierDialogOpen(false);
    setTierDraft(emptyTier());
    setTierDraftErrors({});
    setEditingTierId(null);
    setYearDialogOpen(false);
    setYearDraft(emptyYear());
    setYearDraftErrors({});
    setEditingYearId(null);
  };

  const openYearDialog = () => {
    setEditingYearId(null);
    setYearDraft(emptyYear());
    setYearDraftErrors({});
    setYearDialogOpen(true);
  };

  const openEditYearDialog = (year) => {
    setEditingYearId(year.id);
    setYearDraft({
      id: year.id,
      label: year.label || "",
      startDate: year.startDate || "",
      endDate: year.endDate || "",
      commitment: year.commitment || "",
      discount: year.discount || "",
    });
    setYearDraftErrors({});
    setYearDialogOpen(true);
  };

  const closeYearDialog = () => {
    setYearDialogOpen(false);
    setYearDraft(emptyYear());
    setYearDraftErrors({});
    setEditingYearId(null);
  };

  const setYearDraftField = (key, value) => {
    setYearDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "startDate" && value && prev.endDate && prev.endDate <= value) {
        next.endDate = "";
      }
      return next;
    });
    setYearDraftErrors((prev) => {
      if (!prev[key] && !(key === "startDate" && prev.endDate)) return prev;
      const next = { ...prev };
      delete next[key];
      if (key === "startDate") delete next.endDate;
      return next;
    });
  };

  const saveYearDraft = () => {
    const nextErrors = {};
    if (!yearDraft.label.trim()) nextErrors.label = "Enter a program year identifier.";
    if (yearDraft.startDate && yearDraft.endDate && yearDraft.endDate < yearDraft.startDate) {
      nextErrors.endDate = "End date is before the start date.";
    }
    const commitment = parseNumber(yearDraft.commitment);
    if (yearDraft.commitment.trim() && (Number.isNaN(commitment) || commitment < 0)) {
      nextErrors.commitment = "Enter an amount.";
    }
    const discount = parseNumber(yearDraft.discount);
    if (yearDraft.discount.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) {
      nextErrors.discount = "Enter 0 to 100.";
    }
    setYearDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const normalized = {
      ...yearDraft,
      id: editingYearId || yearDraft.id,
      label: yearDraft.label.trim(),
      commitment: yearDraft.commitment.trim() ? formatCommitment(yearDraft.commitment) : "",
      discount: yearDraft.discount.trim(),
    };

    setForm((prev) => ({
      ...prev,
      years: editingYearId
        ? prev.years.map((row) => (row.id === editingYearId ? normalized : row))
        : [...prev.years, normalized],
    }));
    setErrors((prev) => {
      if (!prev.years?.[editingYearId || normalized.id]) return prev;
      const next = { ...prev, years: { ...prev.years } };
      delete next.years[editingYearId || normalized.id];
      if (!Object.keys(next.years).length) delete next.years;
      return next;
    });
    closeYearDialog();
  };

  const openTierDialog = () => {
    setEditingTierId(null);
    setTierDraft(emptyTier());
    setTierDraftErrors({});
    setTierDialogOpen(true);
  };

  const openEditTierDialog = (tier) => {
    setEditingTierId(tier.id);
    setTierDraft({
      id: tier.id,
      name: tier.name || "",
      startDate: tier.startDate || "",
      endDate: tier.endDate || "",
      threshold: tier.threshold || "",
      discount: tier.discount || "",
    });
    setTierDraftErrors({});
    setTierDialogOpen(true);
  };

  const closeTierDialog = () => {
    setTierDialogOpen(false);
    setTierDraft(emptyTier());
    setTierDraftErrors({});
    setEditingTierId(null);
  };

  const setTierDraftField = (key, value) => {
    setTierDraft((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "startDate" && value && prev.endDate && prev.endDate <= value) {
        next.endDate = "";
      }
      return next;
    });
    setTierDraftErrors((prev) => {
      if (!prev[key] && !(key === "startDate" && prev.endDate)) return prev;
      const next = { ...prev };
      delete next[key];
      if (key === "startDate") delete next.endDate;
      return next;
    });
  };

  const validateTierDraft = () => {
    const next = {};
    const rowNoun = tierDialogIsPpa ? "year" : "tier";
    if (!tierDraft.name.trim()) next.name = `Enter a ${rowNoun} name.`;
    if (tierDraft.startDate && tierDraft.endDate && tierDraft.endDate <= tierDraft.startDate) {
      next.endDate = "End date must be after the start date.";
    }
    const threshold = parseNumber(tierDraft.threshold);
    if (tierDraft.threshold.trim() && (Number.isNaN(threshold) || threshold < 0)) {
      next.threshold = "Enter an amount.";
    }
    const discount = parseNumber(tierDraft.discount);
    if (tierDraft.discount.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) {
      next.discount = "Enter 0 to 100.";
    }
    if (!tierDraft.name.trim() && !tierDraft.startDate && !tierDraft.endDate && !tierDraft.threshold.trim() && !tierDraft.discount.trim()) {
      next.name = editingTierId
        ? `Enter ${rowNoun} details to save this ${rowNoun}.`
        : `Enter ${rowNoun} details to create a ${rowNoun}.`;
    }
    return next;
  };

  const saveTierDraft = () => {
    const nextErrors = validateTierDraft();
    setTierDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    let threshold = tierDraft.threshold.trim();
    if (threshold) {
      const amount = parseNumber(threshold);
      if (amount != null && !Number.isNaN(amount)) threshold = formatCommitment(threshold);
    }
    let discount = tierDraft.discount.trim();
    if (discount) {
      const amount = parseNumber(discount);
      if (amount != null && !Number.isNaN(amount)) discount = amount.toFixed(2);
    }

    const saved = {
      ...tierDraft,
      id: editingTierId || tierDraft.id,
      name: tierDraft.name.trim(),
      threshold,
      discount,
    };

    setForm((prev) => ({
      ...prev,
      tiers: editingTierId
        ? prev.tiers.map((tier) => (tier.id === editingTierId ? saved : tier))
        : [...prev.tiers, saved],
    }));
    setErrors((prev) => {
      if (!editingTierId || !prev.tiers?.[editingTierId]) return prev;
      const next = { ...prev, tiers: { ...prev.tiers } };
      delete next.tiers[editingTierId];
      if (!Object.keys(next.tiers).length) delete next.tiers;
      return next;
    });
    closeTierDialog();
  };

  const formatTierDate = (isoDate) => {
    if (!isoDate) return "—";
    const parsed = parse(isoDate, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : isoDate;
  };

  const validate = (draft) => {
    const next = {};
    const fields = fieldsIn(config).filter((field) => field.kind !== "tiers" && field.kind !== "years");
    const has = (key) => fields.some((field) => field.key === key);
    if (has("displayName") && !form.displayName.trim()) next.displayName = "Enter a program display name.";
    if (!draft && has("agreementId") && !form.agreementId.trim()) next.agreementId = "Enter an agreement ID.";
    fields.forEach((field) => {
      if (field.kind !== "days") return;
      const raw = String(form[field.key] || "").trim();
      if (raw && !/^\d+$/.test(raw)) next[field.key] = "Enter a whole number of days.";
    });
    if (has("startDate") && has("endDate") && form.startDate && form.endDate && form.endDate < form.startDate) {
      next.endDate = "End date must be on or after the start date.";
    }
    fields.forEach((field) => {
      const raw = form[field.key];
      if (field.kind !== "percent" && field.kind !== "currency") return;
      if (!String(raw || "").trim()) return;
      const amount = parseNumber(raw);
      if (field.kind === "percent" && (Number.isNaN(amount) || amount < 0 || amount > 100)) {
        next[field.key] = "Enter a value from 0 to 100.";
      }
      if (field.kind === "currency" && (Number.isNaN(amount) || amount < 0)) {
        next[field.key] = field.amountError || "Enter an amount.";
      }
    });
    if (hasYearsSection) {
      const yearErrors = {};
      (form.years || []).forEach((year) => {
        const blank =
          !year.label?.trim() &&
          !year.startDate &&
          !year.endDate &&
          !year.commitment?.trim() &&
          !year.discount?.trim();
        if (blank) return;
        const row = {};
        if (!year.label?.trim()) row.label = "Enter a program year identifier.";
        if (year.startDate && year.endDate && year.endDate < year.startDate) {
          row.endDate = "End date is before the start date.";
        }
        const commitment = parseNumber(year.commitment);
        if (year.commitment?.trim() && (Number.isNaN(commitment) || commitment < 0)) {
          row.commitment = "Enter an amount.";
        }
        const discount = parseNumber(year.discount);
        if (year.discount?.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) {
          row.discount = "Enter 0 to 100.";
        }
        if (Object.keys(row).length) yearErrors[year.id] = row;
      });
      if (Object.keys(yearErrors).length) next.years = yearErrors;
    }
    if (fieldsIn(config).some((field) => field.kind === "tiers")) {
      const tierErrors = {};
      form.tiers.forEach((tier) => {
        const blank = !tier.name.trim() && !tier.startDate && !tier.endDate && !tier.threshold.trim() && !tier.discount.trim();
        if (blank) return;
        const row = {};
        if (!tier.name.trim()) row.name = "Enter a tier name.";
        if (tier.startDate && tier.endDate && tier.endDate < tier.startDate) row.endDate = "End date is before the start date.";
        const threshold = parseNumber(tier.threshold);
        if (tier.threshold.trim() && (Number.isNaN(threshold) || threshold < 0)) row.threshold = "Enter an amount.";
        const discount = parseNumber(tier.discount);
        if (tier.discount.trim() && (Number.isNaN(discount) || discount < 0 || discount > 100)) {
          row.discount = "Enter 0 to 100.";
        }
        if (Object.keys(row).length) tierErrors[tier.id] = row;
      });
      if (Object.keys(tierErrors).length) next.tiers = tierErrors;
    }
    return next;
  };

  const submit = async (status) => {
    if (saving) return;
    const draft = status === "draft";
    let nextErrors = validate(draft);
    if (!draft) {
      nextErrors = { ...nextErrors, ...validateActiveProgram(form) };
    }
    setErrors(nextErrors);
    setFormError("");
    if (Object.keys(nextErrors).length) {
      document.querySelector("[aria-invalid='true']")?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    const payload = buildProgramCreatePayload(
      {
        ...form,
        accountPayerByNumber: {
          ...(form.payeePayerByNumber || {}),
          ...accountPayerByNumber,
        },
      },
      status
    );
    setSaving(true);
    try {
      const saved = isEdit
        ? await updatePartnerProgram(toApiProgramType(form.programType), programId, payload)
        : await createPartnerProgram(payload);
      const typeLabel = toApiProgramType(form.programType);
      const name = saved?.program_display_name || form.displayName.trim() || typeLabel;
      const notice = draft
        ? `${typeLabel} draft saved${name ? `: ${name}` : ""}.`
        : isEdit
          ? `${typeLabel} updated${name ? `: ${name}` : ""}.`
          : `${typeLabel} created${name ? `: ${name}` : ""}.`;
      history.push("/partner-central/dashboard", { notice });
    } catch (error) {
      setSaving(false);
      setFormError(error?.message || "Unable to save the program.");
    }
  };

  const leave = () => history.push("/partner-central/dashboard");

  const helperProps = (key) => ({ sx: { mx: 0, color: errors[key] ? undefined : AH_MUTED } });

  const renderText = (field) => {
    const numeric = field.kind === "percent" || field.kind === "currency";
    const adornmentKind =
      field.adornment
      || (field.kind === "percent" ? "percent" : field.kind === "currency" ? "dollar" : null);
    const adornment = adornmentKind === "dollar"
      ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
      : adornmentKind === "percent"
        ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
        : undefined;
    const inlineHint = field.hintRow ? "" : (field.hint || "");
    return (
      <Box sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column" }}>
        <FieldLabel htmlFor={field.key}>{field.label}</FieldLabel>
        <TextField
          id={field.key}
          fullWidth
          size="small"
          value={form[field.key]}
          onChange={(event) => {
            const value = field.kind === "days" ? event.target.value.replace(/[^\d]/g, "") : event.target.value;
            setField(field.key, value);
          }}
          onBlur={() => {
            if (!numeric || !String(form[field.key] || "").trim() || errors[field.key]) return;
            const amount = parseNumber(form[field.key]);
            if (amount == null || Number.isNaN(amount)) return;
            setField(field.key, field.kind === "percent" ? amount.toFixed(2) : formatCommitment(form[field.key]));
          }}
          placeholder={field.placeholder || ""}
          error={Boolean(errors[field.key])}
          helperText={errors[field.key] || inlineHint}
          FormHelperTextProps={helperProps(field.key)}
          InputProps={adornment}
          inputProps={{ inputMode: field.kind === "days" ? "numeric" : numeric ? "decimal" : "text" }}
          sx={fieldSx}
        />
      </Box>
    );
  };

  const renderSelect = (field) => (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel htmlFor={field.key}>{field.label}</FieldLabel>
      <TextField
        id={field.key}
        select
        fullWidth
        size="small"
        value={form[field.key] || ""}
        onChange={(event) => setField(field.key, event.target.value)}
        error={Boolean(errors[field.key])}
        helperText={errors[field.key] || field.hint || ""}
        FormHelperTextProps={helperProps(field.key)}
        SelectProps={buildSelectProps({ placeholder: field.placeholder || "Select..." })}
        sx={selectFieldSx(Boolean(form[field.key]))}
      >
        {field.options.map((option) => (
          <MenuItem key={option} value={option} sx={menuItemSx}>{option}</MenuItem>
        ))}
      </TextField>
    </Box>
  );

  const renderDate = (field) => {
    const isEndDate = field.key === "endDate";
    const endMinDate = isEndDate ? dayAfterIso(form.startDate) : null;

    return (
      <Box sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column" }}>
        <FieldLabel htmlFor={field.key}>{field.label}</FieldLabel>
        <DueDateField
          id={field.key}
          value={form[field.key]}
          onChange={(value) => {
            if (field.key === "startDate") {
              setForm((prev) => ({
                ...prev,
                startDate: value,
                endDate: value && prev.endDate && prev.endDate <= value ? "" : prev.endDate,
              }));
              setErrors((prev) => {
                if (!prev.startDate && !prev.endDate) return prev;
                const next = { ...prev };
                delete next.startDate;
                delete next.endDate;
                return next;
              });
              return;
            }
            setField(field.key, value);
          }}
          showLabel={false}
          size="small"
          minDate={isEndDate ? endMinDate : null}
          ariaLabel={field.label}
          placeholder={field.placeholder || "mm/dd/yyyy"}
          error={Boolean(errors[field.key])}
          helperText={errors[field.key] || ""}
          portalId="root"
        />
      </Box>
    );
  };

  const renderAccounts = (field) => (
    <Box sx={{ minWidth: 0 }}>
      <PayerPayeeAccountsField
        key={isEdit ? `edit-${programTypeParam}-${programId}` : "create"}
        id={field.key}
        label={field.label}
        placeholder={field.placeholder}
        hint={field.hint}
        payerOptions={payerAccounts}
        linkedAccounts={payerAccounts}
        payerAccountNumbers={form.payerAccountNumbers}
        payeeAccountNumbers={form.payeeAccountNumbers}
        payeeUiPayerNumbers={form.payeeUiPayerNumbers}
        onPayersChange={(value) => setField("payerAccountNumbers", value)}
        onPayeesChange={(value) => setField("payeeAccountNumbers", value)}
        onAccountsMapChange={(map, meta = {}) => {
          setForm((prev) => {
            const nextMap = map && typeof map === "object" ? map : {};
            const payeePayerByNumber = { ...(prev.payeePayerByNumber || {}) };
            Object.entries(nextMap).forEach(([payer, payees]) => {
              (Array.isArray(payees) ? payees : []).forEach((payee) => {
                const id = String(payee || "").trim();
                if (/^\d{12}$/.test(id) && id !== String(payer).trim()) {
                  payeePayerByNumber[id] = String(payer).trim();
                }
              });
            });
            const next = {
              ...prev,
              payerPayeeAccountsMap: nextMap,
              payeePayerByNumber,
            };
            // Atomic with map when the field provides the full selection snapshot.
            if (Array.isArray(meta.payerAccountNumbers)) {
              next.payerAccountNumbers = meta.payerAccountNumbers;
            }
            if (Array.isArray(meta.payeeAccountNumbers)) {
              next.payeeAccountNumbers = meta.payeeAccountNumbers;
            }
            if (Array.isArray(meta.payeeUiPayerNumbers)) {
              next.payeeUiPayerNumbers = meta.payeeUiPayerNumbers;
            }
            return next;
          });
          setErrors((prev) => {
            if (!prev.payerAccountNumbers && !prev.payeeAccountNumbers) return prev;
            const next = { ...prev };
            delete next.payerAccountNumbers;
            delete next.payeeAccountNumbers;
            return next;
          });
        }}
        fieldSx={fieldSx}
      />
      {errors.payerAccountNumbers ? (
        <Typography sx={{ mt: 0.5, fontSize: 12, color: "#d32f2f" }}>{errors.payerAccountNumbers}</Typography>
      ) : null}
    </Box>
  );

  const toggleRegion = (code) => {
    setForm((prev) => {
      const next = prev.regions.includes(code)
        ? prev.regions.filter((region) => region !== code)
        : [...prev.regions, code];
      return { ...prev, regions: next };
    });
    setErrors((prev) => {
      if (!prev.regions) return prev;
      const next = { ...prev };
      delete next.regions;
      return next;
    });
  };

  const renderRegions = (field) => {
    const allRegions = form.regions.length === 0;
    return (
      <Box sx={{ minWidth: 0 }}>
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: `1px solid ${AH_BORDER}`,
            borderRadius: "8px !important",
            backgroundColor: AH_WHITE,
            "&:before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: AH_MUTED }} />}
            aria-controls={`${field.key}-panel`}
            id={`${field.key}-header`}
            sx={{
              minHeight: 48,
              px: 1.5,
              "& .MuiAccordionSummary-content": {
                my: 1,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                pr: 1,
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }}>
                {field.label}
              </Typography>
              {field.hint ? (
                <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                  {field.hint}
                </Typography>
              ) : null}
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_MUTED, flexShrink: 0 }}>
              {regionsSummary(form.regions)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={`${field.key}-panel`}
            sx={{
              px: 1.5,
              pt: 0,
              pb: 1.5,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: AH_WHITE,
            }}
          >
            <Box
              role="group"
              aria-label={field.label}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                pt: 1.5,
              }}
            >
              <Chip
                label="All Regions"
                clickable
                aria-pressed={allRegions}
                onClick={() => setField("regions", [])}
                sx={regionChipSx(allRegions)}
              />
              {AWS_REGION_CODES.map((code) => {
                const selected = form.regions.includes(code);
                return (
                  <Chip
                    key={code}
                    label={code}
                    clickable
                    aria-pressed={selected}
                    onClick={() => toggleRegion(code)}
                    sx={regionChipSx(selected)}
                  />
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const toggleService = (key, service) => {
    const selected = form[key] || [];
    const next = selected.includes(service)
      ? selected.filter((item) => item !== service)
      : [...selected, service];
    setField(key, next);
  };

  const renderServices = (field) => {
    const selected = form[field.key] || [];
    const selectedSet = new Set(selected);
    const query = serviceSearch.trim().toLowerCase();
    const matchesQuery = (service) => !query || service.toLowerCase().includes(query);
    // Selected chips first (selection order), then remaining catalog matches.
    const visibleServices = [
      ...selected.filter(matchesQuery),
      ...eligibleServicesCatalog.filter(
        (service) => !selectedSet.has(service) && matchesQuery(service)
      ),
    ];
    const visibleSelectedCount = visibleServices.filter((service) => selectedSet.has(service)).length;
    const allVisibleSelected =
      visibleServices.length > 0 && visibleSelectedCount === visibleServices.length;

    let emptyMessage = field.noOptionsText || "No services match your search.";
    if (eligibleServicesLoading) emptyMessage = "Loading services…";
    else if (eligibleServicesError) emptyMessage = eligibleServicesError;
    else if (!eligibleServicesCatalog.length && !selected.length) {
      emptyMessage = "No services available.";
    }

    return (
      <Box sx={{ minWidth: 0 }}>
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            border: `1px solid ${AH_BORDER}`,
            borderRadius: "8px !important",
            backgroundColor: AH_WHITE,
            "&:before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: AH_MUTED }} />}
            aria-controls={`${field.key}-panel`}
            id={`${field.key}-header`}
            sx={{
              minHeight: 48,
              px: 1.5,
              "& .MuiAccordionSummary-content": {
                my: 1,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                pr: 1,
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }}>
                {field.label}
              </Typography>
              {field.hint ? (
                <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.25 }}>
                  {field.hint}
                </Typography>
              ) : null}
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: AH_MUTED, flexShrink: 0 }}>
              {servicesSummary(selected)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            id={`${field.key}-panel`}
            sx={{
              px: 1.5,
              pt: 0,
              pb: 1.5,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: AH_WHITE,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
                pt: 1.5,
                mb: 1.25,
              }}
            >
              <TextField
                size="small"
                value={serviceSearch}
                onChange={(event) => setServiceSearch(event.target.value)}
                placeholder={field.placeholder || "Search services..."}
                inputProps={{ "aria-label": `Search ${field.label}` }}
                disabled={eligibleServicesLoading}
                sx={{ ...fieldSx, flex: "1 1 220px", minWidth: 0, maxWidth: { sm: 360 } }}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={eligibleServicesLoading || !visibleServices.length}
                onClick={() => {
                  if (allVisibleSelected) {
                    const remove = new Set(visibleServices);
                    setField(field.key, selected.filter((service) => !remove.has(service)));
                    return;
                  }
                  setField(field.key, Array.from(new Set([...selected, ...visibleServices])));
                }}
                sx={{ ...outlinedButtonSx, height: 36, minHeight: 36, px: 1.5 }}
              >
                {allVisibleSelected ? "Clear All" : "Select All"}
              </Button>
            </Box>
            <Box
              role="group"
              aria-label={field.label}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                maxHeight: 280,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {eligibleServicesLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
                  <CircularProgress size={18} sx={{ color: AH_ACCENT }} />
                  <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
                    Loading services…
                  </Typography>
                </Box>
              ) : visibleServices.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: AH_MUTED, py: 1 }}>
                  {emptyMessage}
                </Typography>
              ) : (
                visibleServices.map((service) => {
                  const isSelected = selectedSet.has(service);
                  return (
                    <Chip
                      key={service}
                      label={service}
                      clickable
                      title={service}
                      aria-pressed={isSelected}
                      onClick={() => toggleService(field.key, service)}
                      sx={serviceChipSx(isSelected)}
                    />
                  );
                })
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const renderSwitch = (field) => (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        border: `1px solid ${AH_BORDER}`,
        borderRadius: "8px",
        backgroundColor: AH_WHITE,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT, lineHeight: 1.4 }}>{field.label}</Typography>
        {field.hint ? <Typography sx={{ ...hintSx, mt: 0.25 }}>{field.hint}</Typography> : null}
      </Box>
      <Switch
        checked={Boolean(form[field.key])}
        onChange={(event) => setField(field.key, event.target.checked)}
        inputProps={{ "aria-label": field.label }}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": { color: AH_ACCENT },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: AH_ACCENT },
        }}
      />
    </Box>
  );

  const renderField = (field) => {
    if (field.kind === "select") return renderSelect(field);
    if (field.kind === "date") return renderDate(field);
    if (field.kind === "accounts") return renderAccounts(field);
    if (field.kind === "regions") return renderRegions(field);
    if (field.kind === "services") return renderServices(field);
    if (field.kind === "switch") return renderSwitch(field);
    return renderText(field);
  };

  const renderYears = () => {
    const cellSx = formTableBodyCellSx;
    const headSx = formTableHeadCellSx;
    return (
      <TableContainer sx={formTableCardSx}>
        <Table size="small" aria-label="Annual commitment breakdown">
          <TableHead>
            <TableRow>
              {["YEAR", "START", "END", "COMMITMENT ($)", "DISCOUNT (%)", "ACTIONS"].map((heading) => (
                <TableCell key={heading} sx={headSx}>{heading}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(form.years || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ ...cellSx, py: 4, borderBottom: 0, color: AH_MUTED }}>
                  No annual commitment rows yet.
                </TableCell>
              </TableRow>
            ) : (form.years || []).map((year) => {
              const rowErrors = errors.years?.[year.id] || {};
              return (
                <TableRow key={year.id} hover>
                  <TableCell sx={{ ...cellSx, minWidth: 120 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.label ? "#d32f2f" : AH_TEXT, fontWeight: 600 }}>
                      {year.label || "—"}
                    </Typography>
                    {rowErrors.label ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.label}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 110 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: "#334155" }}>{formatTierDate(year.startDate)}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 110 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.endDate ? "#d32f2f" : "#334155" }}>
                      {formatTierDate(year.endDate)}
                    </Typography>
                    {rowErrors.endDate ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.endDate}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 130 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.commitment ? "#d32f2f" : "#334155" }}>
                      {year.commitment ? `$${year.commitment}` : "—"}
                    </Typography>
                    {rowErrors.commitment ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.commitment}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 100 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.discount ? "#d32f2f" : "#334155" }}>
                      {year.discount ? `${year.discount}%` : "—"}
                    </Typography>
                    {rowErrors.discount ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.discount}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: 96, whiteSpace: "nowrap" }}>
                    <Box sx={formTableActionsWrapSx}>
                      <Tooltip title="Edit year">
                        <IconButton
                          aria-label={`Edit ${year.label || "year"}`}
                          onClick={() => openEditYearDialog(year)}
                          size="small"
                          sx={formTableActionBtnSx}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove year">
                        <IconButton
                          aria-label={`Remove ${year.label || "year"}`}
                          onClick={() => setForm((prev) => ({
                            ...prev,
                            years: (prev.years || []).filter((row) => row.id !== year.id),
                          }))}
                          size="small"
                          sx={formTableDeleteBtnSx}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTiers = (variant) => {
    const ranged = variant === "ppa" || variant === "spp";
    const isPpaYears = variant === "ppa";
    const rowNoun = isPpaYears ? "year" : "tier";
    const nameHeading = isPpaYears ? "YEAR NAME" : "TIER NAME";
    const dateRangeLabel = variant === "spp"
      ? "EVALUATION DATE RANGE"
      : "PROGRAM YEAR / EFFECTIVE DATE RANGE";
    const discountLabel = variant === "spp" ? "ACCELERATED DISCOUNT (%)" : "DISCOUNT (%)";
    const cellSx = formTableBodyCellSx;
    const headSx = formTableHeadCellSx;
    return (
      <TableContainer sx={formTableCardSx}>
        <Table
          size="small"
          aria-label={isPpaYears ? "Annual commitment breakdown" : "Volume tier discounts"}
        >
          <TableHead>
            <TableRow>
              {ranged ? (
                <>
                  <TableCell rowSpan={2} sx={{ ...headSx, verticalAlign: "bottom" }}>{nameHeading}</TableCell>
                  <TableCell colSpan={2} sx={headSx}>{dateRangeLabel}</TableCell>
                  <TableCell rowSpan={2} sx={{ ...headSx, verticalAlign: "bottom" }}>MIN ANNUAL SPEND ($)</TableCell>
                  <TableCell rowSpan={2} sx={{ ...headSx, verticalAlign: "bottom" }}>{discountLabel}</TableCell>
                  <TableCell rowSpan={2} sx={{ ...headSx, verticalAlign: "bottom" }}>ACTIONS</TableCell>
                </>
              ) : (
                ["TIER NAME", "START DATE", "END DATE", "THRESHOLD ($)", "DISCOUNT (%)", "ACTIONS"].map((heading) => (
                  <TableCell key={heading} sx={headSx}>{heading}</TableCell>
                ))
              )}
            </TableRow>
            {ranged ? (
              <TableRow>
                {["START", "END"].map((heading) => (
                  <TableCell key={heading} sx={headSx}>{heading}</TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableHead>
          <TableBody>
            {form.tiers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ ...cellSx, py: 4, borderBottom: 0, color: AH_MUTED }}>
                  {isPpaYears ? "No years yet." : "No volume tiers yet."}
                </TableCell>
              </TableRow>
            ) : form.tiers.map((tier) => {
              const rowErrors = errors.tiers?.[tier.id] || {};
              const hasRowError = Boolean(rowErrors.name || rowErrors.endDate || rowErrors.threshold || rowErrors.discount);
              return (
                <TableRow key={tier.id} hover>
                  <TableCell sx={{ ...cellSx, minWidth: ranged ? 180 : 140 }}>
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: hasRowError && rowErrors.name ? "#d32f2f" : AH_TEXT }}>
                      {tier.name || "—"}
                    </Typography>
                    {rowErrors.name ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.name}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 110 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: "#334155" }}>{formatTierDate(tier.startDate)}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 110 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.endDate ? "#d32f2f" : "#334155" }}>
                      {formatTierDate(tier.endDate)}
                    </Typography>
                    {rowErrors.endDate ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.endDate}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: ranged ? 140 : 110 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.threshold ? "#d32f2f" : "#334155" }}>
                      {tier.threshold ? `$${tier.threshold}` : "—"}
                    </Typography>
                    {rowErrors.threshold ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.threshold}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, minWidth: 100 }}>
                    <Typography sx={{ fontSize: "0.8125rem", color: rowErrors.discount ? "#d32f2f" : "#334155" }}>
                      {tier.discount ? `${tier.discount}%` : "—"}
                    </Typography>
                    {rowErrors.discount ? (
                      <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>{rowErrors.discount}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: 96, whiteSpace: "nowrap" }}>
                    <Box sx={formTableActionsWrapSx}>
                      <Tooltip title={`Edit ${rowNoun}`}>
                        <IconButton
                          aria-label={`Edit ${tier.name || rowNoun}`}
                          onClick={() => openEditTierDialog(tier)}
                          size="small"
                          sx={formTableActionBtnSx}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Remove ${rowNoun}`}>
                        <IconButton
                          aria-label={`Remove ${tier.name || rowNoun}`}
                          onClick={() => setForm((prev) => ({ ...prev, tiers: prev.tiers.filter((row) => row.id !== tier.id) }))}
                          size="small"
                          sx={formTableDeleteBtnSx}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!allowed) return null;

  const formTitle = isEdit ? "Edit Program" : "Add Program";
  const formSubtitle = isEdit
    ? "Update this wholesale program agreement and commercial terms."
    : config.subtitle;

  return (
    <AuditHubThemeProvider>
      <Dialog
        open
        onClose={saving ? undefined : leave}
        fullWidth
        maxWidth="md"
        scroll="paper"
        aria-labelledby="add-program-title"
        PaperProps={{
          sx: {
            ...auditHubPageSx,
            borderRadius: "12px",
            overflow: "hidden",
            maxWidth: 760,
            maxHeight: { xs: "94vh", sm: "90vh" },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            minHeight: 56,
            px: 3,
            flexShrink: 0,
            backgroundColor: AH_ACCENT,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id="add-program-title"
              sx={{
                fontWeight: 700,
                fontSize: "1.0625rem",
                color: AH_WHITE,
                lineHeight: 1.3,
              }}
            >
              {formTitle}
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 12,
                color: AH_WHITE,
                opacity: 0.92,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formSubtitle}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            disabled={saving}
            onClick={leave}
            sx={{
              p: 0,
              m: 0,
              border: "none",
              background: "transparent",
              color: AH_WHITE,
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              cursor: saving ? "default" : "pointer",
              flexShrink: 0,
            }}
          >
            ×
          </Box>
        </Box>

        <DialogContent
          dividers={false}
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.5,
            backgroundColor: AH_WHITE,
            flex: "1 1 auto",
            overflowY: "auto",
          }}
        >
              {loadingRecord ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={32} sx={{ color: AH_ACCENT }} />
                </Box>
              ) : null}

              {!loadingRecord && config.sections.map((section, index) => (
                <Box key={section.title}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: index === 0 ? 0 : 2.5, mb: 1.5 }}>
                    <Typography component="h3" sx={{ ...sectionSx, mt: 0, mb: 0 }}>{section.title}</Typography>
                    {section.action === "addYear" ? (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                        onClick={openYearDialog}
                        sx={{ ...accentOutlinedButtonSx, height: 36, minHeight: 36, px: 1.5 }}
                      >
                        Add Year
                      </Button>
                    ) : null}
                    {section.action === "addTier" ? (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                        onClick={openTierDialog}
                        sx={{ ...accentOutlinedButtonSx, height: 36, minHeight: 36, px: 1.5 }}
                      >
                        {form.programType === "PPA" ? "Add Year" : "Add Tier"}
                      </Button>
                    ) : null}
                  </Box>
                  {index === 0 ? (
                    <Box sx={{ mb: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="program-type">Program Type</FieldLabel>
                        <TextField
                          id="program-type"
                          select
                          fullWidth
                          size="small"
                          value={form.programType}
                          onChange={(event) => chooseProgram(event.target.value)}
                          disabled={isEdit}
                          helperText={isEdit ? "Program type cannot be changed after create." : ""}
                          FormHelperTextProps={{ sx: { mx: 0, color: AH_MUTED } }}
                          SelectProps={buildSelectProps({
                            renderValue: (selected) =>
                              PROGRAM_TYPES.find((option) => option.code === selected)?.label || selected || "",
                          })}
                          sx={selectFieldSx(Boolean(form.programType))}
                        >
                          {PROGRAM_TYPES.map((option) => (
                            <MenuItem key={option.code} value={option.code} sx={menuItemSx}>{option.label}</MenuItem>
                          ))}
                        </TextField>
                      </Box>
                      {(() => {
                        const displayNameField = section.blocks
                          .flatMap((block) => (block.kind === "tiers" || block.kind === "years" ? [] : block.fields))
                          .find((field) => field.key === "displayName");
                        return displayNameField ? renderField(displayNameField) : null;
                      })()}
                    </Box>
                  ) : null}
                  {section.blocks.map((block, blockIndex) => {
                    const fields = block.kind === "tiers" || block.kind === "years"
                      ? null
                      : block.fields.filter((field) => !(index === 0 && field.key === "displayName"));
                    if (fields && fields.length === 0) return null;
                    return (
                      <Box
                        key={`${section.title}-${blockIndex}`}
                        sx={{
                          mt: blockIndex === 0 && index !== 0 ? 0 : blockIndex === 0 ? 0 : 2,
                          ...(block.kind === "tiers" || block.kind === "years"
                            ? {}
                            : {
                                display: "grid",
                                gridTemplateColumns: block.columns || "1fr",
                                gap: 2,
                                alignItems: "start",
                                width: "100%",
                              }),
                        }}
                      >
                        {block.kind === "years" ? renderYears() : block.kind === "tiers" ? renderTiers(block.variant) : (
                          <>
                            {fields.map((field) => (
                              <React.Fragment key={field.key}>{renderField(field)}</React.Fragment>
                            ))}
                            {fields
                              .filter((field) => field.hintRow && field.hint && !errors[field.key])
                              .map((field) => (
                                <Typography
                                  key={`${field.key}-hint-row`}
                                  sx={{
                                    ...hintSx,
                                    gridColumn: "1 / -1",
                                    mt: -0.5,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {field.hint}
                                </Typography>
                              ))}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}

              {!loadingRecord && formError ? (
                <Typography sx={{ mt: 2.5, fontSize: 13, color: "#d32f2f", lineHeight: 1.5 }} role="alert">
                  {formError}
                </Typography>
              ) : null}
        </DialogContent>

        {!loadingRecord ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 3,
              py: 2,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: "#f8fafc",
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={leave}
              disabled={saving}
              sx={{
                ...outlinedButtonSx,
                backgroundColor: "#74788d",
                color: AH_WHITE,
                borderColor: "#74788d",
                "&:hover": { backgroundColor: "#686c7f", borderColor: "#686c7f", boxShadow: "none" },
              }}
            >
              Cancel
            </Button>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ml: "auto" }}>
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                onClick={() => submit("draft")}
                disabled={saving}
                sx={{
                  ...outlinedButtonSx,
                  backgroundColor: AH_WHITE,
                  color: AH_ACCENT,
                  borderColor: AH_ACCENT,
                  "&:hover": {
                    backgroundColor: AH_WHITE,
                    color: AH_ACCENT_HOVER,
                    borderColor: AH_ACCENT_HOVER,
                    boxShadow: "none",
                  },
                }}
              >
                {saving ? "Saving…" : "Save Draft"}
              </Button>
              <Button variant="contained" onClick={() => submit("saved")} disabled={saving} sx={primaryButtonSx}>
                {saving ? "Saving…" : isEdit ? "Update Program" : config.saveLabel}
              </Button>
            </Box>
          </Box>
        ) : null}
      </Dialog>

        <Dialog
          open={yearDialogOpen}
          onClose={closeYearDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              ...auditHubPageSx,
              borderRadius: "12px",
              overflow: "hidden",
              maxWidth: 560,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 56,
              px: 3,
              backgroundColor: AH_ACCENT,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
              {editingYearId ? "Edit Program Year" : "Add Program Year"}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-label="Close"
              onClick={closeYearDialog}
              sx={{
                p: 0,
                m: 0,
                border: "none",
                background: "transparent",
                color: AH_WHITE,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ×
            </Box>
          </Box>
          <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="year-draft-label">Program Year Identifier</FieldLabel>
                <TextField
                  id="year-draft-label"
                  size="small"
                  fullWidth
                  value={yearDraft.label}
                  onChange={(event) => setYearDraftField("label", event.target.value)}
                  placeholder="e.g., Year 1"
                  error={Boolean(yearDraftErrors.label)}
                  helperText={yearDraftErrors.label || ""}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0, width: "100%" }}>
                  <FieldLabel htmlFor="year-draft-start">Annual Commitment Start Date</FieldLabel>
                  <DueDateField
                    id="year-draft-start"
                    value={yearDraft.startDate}
                    onChange={(value) => setYearDraftField("startDate", value)}
                    fullWidth
                    showLabel={false}
                    size="small"
                    minDate={null}
                    ariaLabel="Annual commitment start date"
                    placeholder="Select the Start Date"
                    portalId="root"
                  />
                </Box>
                <Box sx={{ minWidth: 0, width: "100%" }}>
                  <FieldLabel htmlFor="year-draft-end">Annual Commitment End Date</FieldLabel>
                  <DueDateField
                    id="year-draft-end"
                    value={yearDraft.endDate}
                    onChange={(value) => setYearDraftField("endDate", value)}
                    fullWidth
                    showLabel={false}
                    size="small"
                    minDate={dayAfterIso(yearDraft.startDate)}
                    ariaLabel="Annual commitment end date"
                    placeholder="Select the End Date"
                    error={Boolean(yearDraftErrors.endDate)}
                    helperText={yearDraftErrors.endDate || ""}
                    portalId="root"
                  />
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="year-draft-commitment">Annual Spend Commitment Target ($)</FieldLabel>
                  <TextField
                    id="year-draft-commitment"
                    size="small"
                    fullWidth
                    value={yearDraft.commitment}
                    onChange={(event) => setYearDraftField("commitment", event.target.value)}
                    onBlur={() => {
                      if (!yearDraft.commitment.trim() || yearDraftErrors.commitment) return;
                      const amount = parseNumber(yearDraft.commitment);
                      if (amount != null && !Number.isNaN(amount)) {
                        setYearDraftField("commitment", formatCommitment(yearDraft.commitment));
                      }
                    }}
                    placeholder="0"
                    error={Boolean(yearDraftErrors.commitment)}
                    helperText={yearDraftErrors.commitment || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="year-draft-discount">Guaranteed Downstream Discount (%)</FieldLabel>
                  <TextField
                    id="year-draft-discount"
                    size="small"
                    fullWidth
                    value={yearDraft.discount}
                    onChange={(event) => setYearDraftField("discount", event.target.value)}
                    placeholder="0.00"
                    error={Boolean(yearDraftErrors.discount)}
                    helperText={yearDraftErrors.discount || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              px: 3,
              py: 2,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: "#f8fafc",
            }}
          >
            <Button
              variant="contained"
              onClick={closeYearDialog}
              sx={{
                ...primaryButtonSx,
                height: 36,
                minHeight: 36,
                backgroundColor: "#74788d",
                color: "#eff2f7",
                "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={saveYearDraft} sx={{ ...primaryButtonSx, height: 36, minHeight: 36 }}>
              {editingYearId ? "Save Year" : "Add Year"}
            </Button>
          </Box>
        </Dialog>

        <Dialog
          open={tierDialogOpen}
          onClose={closeTierDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              ...auditHubPageSx,
              borderRadius: "12px",
              overflow: "hidden",
              maxWidth: 560,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 56,
              px: 3,
              backgroundColor: AH_ACCENT,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
              {tierDialogIsPpa
                ? editingTierId
                  ? "Edit Year"
                  : "Add Year"
                : editingTierId
                  ? "Edit Volume Tier"
                  : "Add Volume Tier"}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-label="Close"
              onClick={closeTierDialog}
              sx={{
                p: 0,
                m: 0,
                border: "none",
                background: "transparent",
                color: AH_WHITE,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ×
            </Box>
          </Box>
          <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="tier-draft-name">
                  {tierDialogIsPpa ? "Year Name" : "Tier Name"}
                </FieldLabel>
                <TextField
                  id="tier-draft-name"
                  size="small"
                  fullWidth
                  value={tierDraft.name}
                  onChange={(event) => setTierDraftField("name", event.target.value)}
                  placeholder={
                    tierDialogIsPpa
                      ? "e.g., Year 1"
                      : tierDialogIsSpp
                        ? "e.g., Tier 1 Growth"
                        : "Tier name"
                  }
                  error={Boolean(tierDraftErrors.name)}
                  helperText={tierDraftErrors.name || ""}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0, width: "100%" }}>
                  <FieldLabel htmlFor="tier-draft-start">
                    {tierDialogIsSpp
                      ? "Evaluation Start Date"
                      : tierDialogIsPpa
                        ? "Effective Start Date"
                        : "Start Date"}
                  </FieldLabel>
                  <DueDateField
                    id="tier-draft-start"
                    value={tierDraft.startDate}
                    onChange={(value) => setTierDraftField("startDate", value)}
                    fullWidth
                    showLabel={false}
                    size="small"
                    minDate={null}
                    ariaLabel={
                      tierDialogIsSpp
                        ? "Evaluation start date"
                        : tierDialogIsPpa
                          ? "Effective start date"
                          : "Tier start date"
                    }
                    placeholder="Select the Start Date"
                    portalId="root"
                  />
                </Box>
                <Box sx={{ minWidth: 0, width: "100%" }}>
                  <FieldLabel htmlFor="tier-draft-end">
                    {tierDialogIsSpp
                      ? "Evaluation End Date"
                      : tierDialogIsPpa
                        ? "Effective End Date"
                        : "End Date"}
                  </FieldLabel>
                  <DueDateField
                    id="tier-draft-end"
                    value={tierDraft.endDate}
                    onChange={(value) => setTierDraftField("endDate", value)}
                    fullWidth
                    showLabel={false}
                    size="small"
                    minDate={dayAfterIso(tierDraft.startDate)}
                    ariaLabel={
                      tierDialogIsSpp
                        ? "Evaluation end date"
                        : tierDialogIsPpa
                          ? "Effective end date"
                          : "Tier end date"
                    }
                    placeholder="Select the End Date"
                    error={Boolean(tierDraftErrors.endDate)}
                    helperText={tierDraftErrors.endDate || ""}
                    portalId="root"
                  />
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="tier-draft-threshold">
                    {tierDialogIsPpa || tierDialogIsSpp
                      ? "Minimum Annual Spend Threshold ($)"
                      : "Threshold ($)"}
                  </FieldLabel>
                  <TextField
                    id="tier-draft-threshold"
                    size="small"
                    fullWidth
                    value={tierDraft.threshold}
                    onChange={(event) => setTierDraftField("threshold", event.target.value)}
                    onBlur={() => {
                      if (!tierDraft.threshold.trim() || tierDraftErrors.threshold) return;
                      const amount = parseNumber(tierDraft.threshold);
                      if (amount != null && !Number.isNaN(amount)) {
                        setTierDraftField("threshold", formatCommitment(tierDraft.threshold));
                      }
                    }}
                    placeholder="0"
                    error={Boolean(tierDraftErrors.threshold)}
                    helperText={tierDraftErrors.threshold || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="tier-draft-discount">
                    {tierDialogIsSpp ? "Accelerated Discount Percentage (%)" : "Discount (%)"}
                  </FieldLabel>
                  <TextField
                    id="tier-draft-discount"
                    size="small"
                    fullWidth
                    value={tierDraft.discount}
                    onChange={(event) => setTierDraftField("discount", event.target.value)}
                    placeholder="0.00"
                    error={Boolean(tierDraftErrors.discount)}
                    helperText={tierDraftErrors.discount || ""}
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              px: 3,
              py: 2,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: "#f8fafc",
            }}
          >
            <Button
              variant="contained"
              onClick={closeTierDialog}
              sx={{
                ...primaryButtonSx,
                height: 36,
                minHeight: 36,
                backgroundColor: "#74788d",
                color: "#eff2f7",
                "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={saveTierDraft} sx={{ ...primaryButtonSx, height: 36, minHeight: 36 }}>
              {editingTierId ? "Save" : "Create"}
            </Button>
          </Box>
        </Dialog>
    </AuditHubThemeProvider>
  );
};

export default AddProgram;
